import React, { useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch } from 'react-router-dom';

import { Button } from 'antd';
import ProviderDashboard from './Dashboard/ProviderDashboard/ProviderDashboard';
import FamilyDashboard from './Dashboard/FamilyDashboard/FamilyDashboard';
import LayoutPage from 'components/Layout';
import LoadingPage from 'components/LoadingPage';
import ProtectedRoute from 'components/ProtectedRoute';
import CheckPermissions from 'components/CheckPermissions';
import { isFamilyRole, isProviderRole, ScrollContext } from 'utils';
import { logout, selectActiveRole, selectRoles } from 'features/auth';
import { ROLES } from 'constants/index';
import { Finance, Logout } from '@carbon/icons-react';
import { Redirect, useHistory } from 'react-router-dom/cjs/react-router-dom';
import { getAdminMenuOptions } from 'containers/Admin/Admin';
import ProviderApplication from './ProviderApplication/ProviderApplication';
import ProviderApplicationNotes from './ProviderApplication/ProviderApplicationNotes';
import ProviderOrganization from './ProviderOrganization/ProviderOrganization';
import MyAccount from 'containers/MyAccount/MyAccount';
import FamilyApplication from './FamilyApplication';
import FamilyApplications from './FamilyApplication/FamilyApplications';
import ProviderUserDetails from './ProviderUserDetails';
import ProviderLocationDetails from './ProviderLocationDetails';
import { ROLES_WITH_ACCESS_TO_USERS_PAGE } from 'containers/Admin/Users/Users';

export default function Internal(props) {
  const history = useHistory();
  const dispatch = useDispatch();

  const activeRole = useSelector(selectActiveRole);
  const roles = useSelector(selectRoles);

  const [scrollHidden, setScrollHidden] = useState(false);

  const menuOptions = useMemo(() => {
    const _options = getInternalMenuItems({ history, roles });

    if (roles.includes(ROLES.SYSTEM_ADMIN)) {
      return [..._options, ...getAdminMenuOptions(roles)];
    }

    return _options;
  }, [history, roles]);

  const _logout = useCallback(async () => {
    dispatch(logout.pending());
  }, [dispatch]);

  return (
    <ScrollContext.Provider value={[scrollHidden, setScrollHidden]}>
      <LayoutPage
        {...props}
        menuOptions={menuOptions}
        headerIcons={
          <Button
            type="text"
            icon={<Logout />}
            style={{
              color: '#FFFFFF',
            }}
            onClick={_logout}
          >
            Logout
          </Button>
        }
      >
        <React.Suspense fallback={<LoadingPage />}>
          <CheckPermissions hideFor={[ROLES.FAMILY, ROLES.PROVIDER]}>
            <InternalUsersRoute activeRole={activeRole} />
          </CheckPermissions>

          {/* </Switch> */}
        </React.Suspense>
      </LayoutPage>
    </ScrollContext.Provider>
  );
}

function InternalUsersRoute({ activeRole }) {
  return (
    <Switch>
      <ProtectedRoute path="/account" message="Navigated to My account">
        <MyAccount />
      </ProtectedRoute>

      <ProtectedRoute
        exact
        path={['/families', '/families/search']}
        message="Navigated to Family Applications Dashboard"
      >
        <FamilyDashboard key="family-applications-dashboard" />
      </ProtectedRoute>
      <ProtectedRoute
        path="/families/:householdId/applications/:id"
        message="Navigated to Family Application Details Page"
      >
        <FamilyApplication />
      </ProtectedRoute>
      <ProtectedRoute path="/families/household/:householdId/" message="Navigated to Family Application Details Page">
        <FamilyApplications />
      </ProtectedRoute>
      <ProtectedRoute path="/families/admin/:adminId/" message="Navigated to Family Application Details Page">
        <FamilyApplications />
      </ProtectedRoute>

      <ProtectedRoute
        exact
        path={['/providers', '/providers/search']}
        message="Navigated to Provider Applications Dashboard"
      >
        <ProviderDashboard key="provider-applications-dashboard" />
      </ProtectedRoute>
      <ProtectedRoute exact path={['/dashboard']} message="Navigated to  Dashboard">
        <Redirect to="/providers" />
      </ProtectedRoute>
      <ProtectedRoute path="/providers/applications/:id/notes" message="Navigated to Application Notes">
        <ProviderApplicationNotes />
      </ProtectedRoute>
      <ProtectedRoute path="/providers/applications/:id" message="Navigated to Application Details Page">
        <ProviderApplication />
      </ProtectedRoute>
      <ProtectedRoute path="/providers/orgs/:id" message="Navigated to Provider Organization Page">
        <ProviderOrganization />
      </ProtectedRoute>
      <ProtectedRoute
        path="/providers/admin/org/:orgId/facility/:facilityId"
        message="Navigated to Provider Location Details Page"
      >
        <ProviderLocationDetails />
      </ProtectedRoute>

      <ProtectedRoute path="/providers/admin/org/:orgId" message="Navigated to Provider User Details Page">
        <ProviderUserDetails />
      </ProtectedRoute>
    </Switch>
  );
}

export function getRedirectPathForInternalUserRole(role) {
  // Provider roles redirect to Provider Application Queue
  const providerRoles = [
    ROLES.PROVIDER_COORDINATOR,
    ROLES.PROVIDER_MANAGER,
    ROLES.PROVIDER_REVIEWER,
    ROLES.PROVIDER_REVIEWER_MANAGER,
    ROLES.PROVIDER_QC_COORDINATOR,
    ROLES.PROVIDER_QC_MANAGER,
    ROLES.PROVIDER_SUPPORT_SPECIALIST,
  ];

  // Family roles redirect to Family Application Queue
  const familyRoles = [
    ROLES.FAMILY_COORDINATOR,
    ROLES.FAMILY_MANAGER,
    ROLES.FAMILY_QC_COORDINATOR,
    ROLES.FAMILY_QC_MANAGER,
  ];

  if (providerRoles.includes(role)) {
    return '/providers';
  } else if (familyRoles.includes(role)) {
    return '/families';
  } else if (role === ROLES.SYSTEM_ADMIN) {
    return '/admin/users';
  }

  return null;
}

export function getInternalMenuItems({ history, roles }) {
  const _isFamilyRole = roles?.some((_role) => isFamilyRole(_role));
  const _isProviderRole = roles?.some((_role) => isProviderRole(_role));

  const items = [
    ...(_isFamilyRole
      ? [
          {
            label: 'Family queue',
            to: '/families',
            match: ['/families', /^\/families\/[0-9]+\/.+/, /^\/families\/household\/[0-9]+\/.+/],
            icon: Finance,
            onClick: () => {
              history.push('/families');
            },
          },
        ]
      : []),
    ...(_isProviderRole
      ? [
          {
            label: 'Provider queue',
            to: '/providers',
            match: ['/providers', '/providers/search'],
            icon: Finance,
            onClick: () => {
              history.push('/providers');
            },
          },
        ]
      : []),
    ...(roles.find((role) => ROLES_WITH_ACCESS_TO_USERS_PAGE.includes(role))
      ? [
          {
            label: 'Users',
            to: '/admin/users',
            match: [
              '/admin/users',
              '/admin/users/internal',
              '/admin/users/providers',
              '/admin/users/families',
              /^\/families\/admin\/[0-9]+\/.+/,
              /^\/providers\/admin\/org\/[0-9]+/,
              /^\/providers\/admin\/org\/[0-9]+\/facility\/[0-9]+/,
            ],
          },
        ]
      : []),
  ];

  return items;
}
